import {announce} from '@github-ui/aria-live'
import {FilterIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Link} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {type RefObject, useEffect, useRef, useState} from 'react'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {IssueTimelineLoading} from '../IssueTimelineLoading'
import {ACTIVITY_FILTER_CATEGORIES, ACTIVITY_FILTER_CATEGORY_IDS, type ActivityFilterCategory} from './activity-filters'
import styles from './ActivityTimelineControls.module.css'
import {CommentsSortMenu} from './CommentsSortMenu'
import {useTimelineItemCounts} from './fetch-timeline-item-counts'
import type {ActivitySort} from './use-activity-timeline-preferences'

type ActivityFilterMenuProps = {
  owner: string
  repo: string
  number: number
  filters: readonly ActivityFilterCategory[]
  onFiltersChange: (filters: ActivityFilterCategory[]) => void
  anchorRef?: RefObject<HTMLButtonElement | null>
}

export function ActivityFilterMenu({
  owner,
  repo,
  number,
  filters,
  onFiltersChange,
  anchorRef,
}: ActivityFilterMenuProps) {
  const [open, setOpen] = useState(false)
  const {data: counts} = useTimelineItemCounts(owner, repo, number, open)
  const filtering = filters.length > 0
  // No filters shows every category, so each one starts checked.
  const shown = filtering ? filters : ACTIVITY_FILTER_CATEGORY_IDS

  const toggle = (category: ActivityFilterCategory) =>
    onFiltersChange(shown.includes(category) ? shown.filter(id => id !== category) : [...shown, category])

  return (
    <ActionMenu open={open} onOpenChange={setOpen} anchorRef={anchorRef}>
      <ActionMenu.Anchor>
        <IconButton
          icon={FilterIcon}
          variant="invisible"
          aria-label="Filter activity"
          data-testid={TEST_IDS.activityFilterMenuButton}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="small">
        <ActionList>
          <ActionList.Group selectionVariant="multiple">
            {ACTIVITY_FILTER_CATEGORIES.map(({id, label}) => {
              const checked = shown.includes(id)
              const locked = checked && shown.length === 1
              return (
                <ActionList.Item
                  key={id}
                  selected={checked}
                  disabled={locked}
                  onSelect={event => {
                    // Keep the menu open so several categories can be picked in one go.
                    event.preventDefault()
                    toggle(id)
                  }}
                >
                  {label}
                  {locked && (
                    <ActionList.Description variant="block">At least one type must be shown</ActionList.Description>
                  )}
                  {counts && <ActionList.TrailingVisual>{counts[id]}</ActionList.TrailingVisual>}
                </ActionList.Item>
              )
            })}
          </ActionList.Group>
          {filtering && (
            <>
              <ActionList.Divider />
              <ActionList.Item onSelect={() => onFiltersChange([])}>Clear filters</ActionList.Item>
            </>
          )}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export function ActivitySortMenu(props: {sort: ActivitySort; onSortChange: (sort: ActivitySort) => void}) {
  return <CommentsSortMenu {...props} subject="activity" iconOnly />
}

export function ActivityLoadingState() {
  const loadingRef = useAnnounceOnMount<HTMLDivElement>(LABELS.timeline.loadingActivity)

  return <IssueTimelineLoading ref={loadingRef} label={LABELS.timeline.loadingActivity} />
}

const SET_ASIDE_NOTICE = 'Showing all activity so the linked event is visible.'
const NO_MATCHING_ACTIVITY = 'No matching activity'

function useAnnounceOnMount<T extends HTMLElement>(message: string) {
  const ref = useRef<T>(null)
  // Announced from the element so it's heard inside the overlay dialog too.
  useEffect(() => {
    announce(message, {element: ref.current ?? undefined})
  }, [message])
  return ref
}

export function ActivityFiltersSetAsideNotice({
  onRestore,
  returnFocusRef,
}: {
  onRestore: () => void
  returnFocusRef?: RefObject<HTMLElement | null>
}) {
  const noticeRef = useAnnounceOnMount<HTMLParagraphElement>(SET_ASIDE_NOTICE)

  return (
    <p ref={noticeRef} className={styles.notice} data-testid={TEST_IDS.activityFiltersSetAsideNotice}>
      {SET_ASIDE_NOTICE}{' '}
      <Link
        as="button"
        inline
        onClick={() => {
          onRestore()
          requestAnimationFrame(() => returnFocusRef?.current?.focus())
        }}
      >
        Use my filters
      </Link>
    </p>
  )
}

export function ActivityFilterEmptyState({
  onClearFilters,
  returnFocusRef,
}: {
  onClearFilters: () => void
  returnFocusRef?: RefObject<HTMLElement | null>
}) {
  const emptyStateRef = useAnnounceOnMount<HTMLDivElement>(NO_MATCHING_ACTIVITY)

  return (
    <div ref={emptyStateRef} data-testid={TEST_IDS.activityFilterEmptyState}>
      <Blankslate narrow>
        <Blankslate.Heading as="h3">{NO_MATCHING_ACTIVITY}</Blankslate.Heading>
        <Blankslate.Description>Nothing on this issue matches the selected filters.</Blankslate.Description>
        <Blankslate.PrimaryAction
          onClick={() => {
            onClearFilters()
            requestAnimationFrame(() => returnFocusRef?.current?.focus())
          }}
        >
          Clear filters
        </Blankslate.PrimaryAction>
      </Blankslate>
    </div>
  )
}
